/**
 * Webhook Activity Tab
 * Displays webhook activity logs for monitoring Square payment events
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Activity, 
  RefreshCw, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  CreditCard,
  FileText,
  Clock,
  Printer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WebhookActivityTab = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, success: 0, failed: 0, other: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchLogs();
  }, [sourceFilter, statusFilter]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/webhook-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setSummary(response.data.summary || { total: 0, success: 0, failed: 0, other: 0 });
    } catch (error) {
      console.error("Error fetching webhook logs:", error);
      toast.error("Failed to load webhook logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLogs();
  };

  const handleClearOldLogs = async () => {
    if (!window.confirm("Delete webhook logs older than 30 days?")) return;
    
    try {
      const response = await axios.delete(`${API}/webhook-logs?older_than_days=30`);
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      console.error("Error clearing logs:", error);
      toast.error("Failed to clear logs");
    }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm("Delete ALL webhook logs? This cannot be undone.")) return;
    
    try {
      const response = await axios.delete(`${API}/webhook-logs/all`);
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      console.error("Error clearing all logs:", error);
      toast.error("Failed to clear all logs");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "unmatched":
        return (
          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Unmatched
          </Badge>
        );
      case "skipped":
        return (
          <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Skipped
          </Badge>
        );
      case "ignored":
        return (
          <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100">
            Ignored
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">{status}</Badge>
        );
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case "square_invoice":
        return <FileText className="w-4 h-4 text-blue-500" />;
      case "square_subscription":
        return <CreditCard className="w-4 h-4 text-purple-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case "square_invoice":
        return "Invoice Payment";
      case "square_subscription":
        return "Subscription";
      case "square_subscription_legacy":
        return "Subscription (Legacy)";
      default:
        return source || "Unknown";
    }
  };

  const handlePrintWebhooks = () => {
    const generateRows = () => {
      return logs.map(log => {
        const statusClass = log.status === 'success' ? 'badge-success' : log.status === 'failed' ? 'badge-failed' : 'badge-pending';
        const time = formatTimestamp(log.timestamp);
        const source = getSourceLabel(log.source);
        const eventType = log.event_type || 'N/A';
        const status = log.status || 'unknown';
        const customer = log.customer_email || log.organization_id || 'N/A';
        const amount = log.amount ? formatAmount(log.amount) : '-';
        const details = log.message || log.error || '-';
        return `<tr>
          <td>${time}</td>
          <td>${source}</td>
          <td>${eventType}</td>
          <td><span class="badge ${statusClass}">${status}</span></td>
          <td>${customer}</td>
          <td>${amount}</td>
          <td>${details}</td>
        </tr>`;
      }).join('');
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Webhook Activity Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a365d; border-bottom: 2px solid #3182ce; padding-bottom: 10px; }
          .stats { display: flex; gap: 30px; margin-bottom: 20px; }
          .stat { background: #f7fafc; padding: 15px; border-radius: 8px; }
          .stat-label { font-size: 12px; color: #718096; }
          .stat-value { font-size: 24px; font-weight: bold; color: #2d3748; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #edf2f7; padding: 12px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #cbd5e0; }
          td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
          tr:nth-child(even) { background: #f7fafc; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
          .badge-success { background: #c6f6d5; color: #276749; }
          .badge-failed { background: #fed7d7; color: #c53030; }
          .badge-pending { background: #fefcbf; color: #975a16; }
          .print-date { text-align: right; color: #718096; font-size: 12px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Webhook Activity Report</h1>
        <p class="print-date">Generated: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Total Events</div><div class="stat-value">${summary.total}</div></div>
          <div class="stat"><div class="stat-label">Success</div><div class="stat-value" style="color: #276749;">${summary.success}</div></div>
          <div class="stat"><div class="stat-label">Failed</div><div class="stat-value" style="color: #c53030;">${summary.failed}</div></div>
          <div class="stat"><div class="stat-label">Other</div><div class="stat-value">${summary.other}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Source</th>
              <th>Event Type</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Amount</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            ${generateRows()}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const formatAmount = (amount, currency = "USD") => {
    if (!amount) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <Card className="border-amber-200 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-amber-600">Loading webhook activity...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 shadow-lg" data-testid="webhook-activity-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Activity className="w-5 h-5" />
              Webhook Activity Log
            </CardTitle>
            <CardDescription>
              Monitor Square payment webhook events and troubleshoot issues
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-amber-300"
              data-testid="refresh-logs-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearOldLogs}
              className="border-amber-300 text-amber-600 hover:bg-amber-50"
              data-testid="clear-old-logs-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear Old
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllLogs}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="clear-all-logs-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintWebhooks}
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              disabled={logs.length === 0}
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-800">{summary.total}</div>
            <div className="text-xs text-amber-600">Total Events</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{summary.success}</div>
            <div className="text-xs text-green-600">Successful</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{summary.failed}</div>
            <div className="text-xs text-red-600">Failed</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-700">{summary.other}</div>
            <div className="text-xs text-gray-600">Other</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="flex-1">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="border-amber-200" data-testid="source-filter">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="square_invoice">Invoice Payments</SelectItem>
                <SelectItem value="square_subscription">Subscriptions</SelectItem>
                <SelectItem value="square_subscription_legacy">Subscriptions (Legacy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-amber-200" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="unmatched">Unmatched</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Logs List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No webhook activity yet</p>
              <p className="text-sm">Events will appear here when Square sends payment notifications</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                data-testid={`webhook-log-${log.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getSourceIcon(log.source)}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">
                          {log.eventType || "Unknown Event"}
                        </span>
                        {getStatusBadge(log.status)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getSourceLabel(log.source)}
                        {log.details?.invoiceNumber && (
                          <span className="ml-2">• Invoice #{log.details.invoiceNumber}</span>
                        )}
                        {log.details?.customerEmail && (
                          <span className="ml-2">• {log.details.customerEmail}</span>
                        )}
                        {log.details?.amount && (
                          <span className="ml-2 font-medium text-green-600">
                            • {formatAmount(log.details.amount, log.details.currency)}
                          </span>
                        )}
                      </div>
                      {log.errorMessage && (
                        <div className="text-sm text-red-600 mt-1">
                          Error: {log.errorMessage}
                        </div>
                      )}
                      {log.details?.reason && (
                        <div className="text-sm text-orange-600 mt-1">
                          {log.details.reason}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {formatTimestamp(log.timestamp)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Help Text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          <strong>What is this?</strong> This log shows all webhook events from Square. 
          When a parent pays an invoice via credit card, Square sends a notification here 
          to automatically mark the invoice as paid. Failed events may indicate configuration issues.
        </div>
      </CardContent>
    </Card>
  );
};

export default WebhookActivityTab;
